// Finance — Overview · KPIs · Cash & Forecast
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";
import { AiSummary, SectionHead, Segmented, Delta, Bar } from "@/components/primitives";
import { useNotImplemented } from "@/hooks/use-not-implemented";
import { WhatIfScreen } from "./WhatIfScenarios";
import { Sparkline, LineChart, BarChart, Donut, FlowChart } from "@/components/charts";
import { fmtUSD, fmtK } from "@/utils/format";
import { DATA } from "@/data";

/* ═══ FINANCE · OVERVIEW ════════════════════════════════════════════ */
const ToneAmt = ({ tone, children }: { tone?: string; children: React.ReactNode }) => {
  const c = ({ neg: "var(--neg)", warn: "var(--warn)", mute: "var(--ink-3)" } as Record<string, string>)[tone || ""] || "var(--ink-2)";
  return <span style={{ color: c, fontWeight: 600 }}>{children}</span>;
};

const ListCard = ({ title, icon, tint, rows }: {
  title: string; icon: string; tint: string;
  rows: { name: string; type: string; amt: number; due: string; tone?: string }[];
}) => {
  const notify = useNotImplemented();
  return (
  <div className="card">
    <div className="card-h">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 26, height: 26, borderRadius: 7, background: tint + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name={icon} size={15} stroke={tint} />
        </span>
        <div className="card-t">{title}</div>
      </div>
      <span className="link" style={{ fontSize: 12.5, cursor: "pointer" }} onClick={() => { posthog.capture("finance_view_all_clicked", { list: title }); notify(`View all — ${title}`); }}>View all</span>
    </div>
    <div>
      {rows.map((r, i) => (
        <div key={i} onClick={() => { posthog.capture("finance_list_row_clicked", { list: title, name: r.name }); notify(r.name); }} style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 18px", borderBottom: i < rows.length - 1 ? "1px solid var(--line-2)" : "none", cursor: "pointer" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 500 }}>{r.name}</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{r.type}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="num" style={{ fontSize: 13.5, fontWeight: 600 }}>{fmtUSD(r.amt)}</div>
            <div style={{ fontSize: 11 }}><ToneAmt tone={r.tone}>{r.due}</ToneAmt></div>
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

export const FinOverview = () => {
  const notify = useNotImplemented();
  const d = DATA;
  const summary = [
    { l: "Revenue", v: "$284,512", sub: "MTD", delta: 12.4, color: "var(--pos)", spark: [2.1, 2.3, 2.2, 2.5, 2.6, 2.7, 2.85] },
    { l: "Expenses", v: "$172,940", sub: "MTD", delta: 4.1, invert: true, color: "var(--warn)", spark: [1.5, 1.55, 1.6, 1.58, 1.65, 1.7, 1.73] },
    { l: "Cash", v: "$842,310", sub: "across 3 accounts", delta: -2.8, invert: true, color: "var(--info)", spark: [9, 8.8, 8.9, 8.6, 8.5, 8.4, 8.42] },
    { l: "Net profit", v: "$111,572", sub: "39% margin", delta: 18.2, color: "var(--brand)", spark: [0.7, 0.8, 0.9, 0.95, 1.0, 1.08, 1.12] },
  ];
  return (
    <div style={{ maxWidth: 1160, margin: "0 auto" }} className="fade-up">
      <SectionHead title="Performance" size="lg"
        sub="How the business is performing over time. Detailed KPIs, cash charts and records live in their own tabs — this is the summary."
        actions={<>
          <button className="btn btn-sec" onClick={() => { posthog.capture("finance_date_range_clicked", { page: "overview" }); notify("Date range filter"); }}><Icon name="calendar" size={15} stroke="var(--ink-3)" /> Last 30 days</button>
          <button className="btn btn-sec" onClick={() => { posthog.capture("finance_export_clicked", { page: "overview" }); notify("Export"); }}><Icon name="download" size={15} stroke="var(--ink-3)" /> Export</button>
        </>} />
      <AiSummary>
        Revenue is up <b>12.4%</b> this month and net margin holds at <b>39%</b>. Expenses rose <b>4.1%</b>, driven by hiring — in line
        with plan. Cash dipped slightly on the annual insurance payment. No unusual risks detected.
      </AiSummary>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 }}>
        {summary.map((s, i) => (
          <div key={i} className="card" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12.5, color: "var(--ink-3)", fontWeight: 500 }}>{s.l}</span>
              <Delta v={s.delta} invert={s.invert} />
            </div>
            <div className="num" style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-.02em", marginTop: 6 }}>{s.v}</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}>{s.sub}</div>
            <div style={{ marginTop: 10 }}><Sparkline data={s.spark} color={s.color} h={26} /></div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-h">
            <div className="card-t">Revenue vs. Expenses</div>
            <Segmented size="sm" value="6m" onChange={(v) => posthog.capture("finance_chart_range_changed", { chart: "revenue_vs_expenses", range: v })} options={[{ value: "3m", label: "3M" }, { value: "6m", label: "6M" }, { value: "12m", label: "12M" }]} />
          </div>
          <div className="card-b">
            <BarChart height={220} keys={["rev", "exp"]} colors={["var(--brand)", "var(--surface-3)"]} yFmt={(v) => fmtK(v * 1000)}
              data={[
                { label: "Dec", rev: 220, exp: 150 }, { label: "Jan", rev: 238, exp: 158 }, { label: "Feb", rev: 229, exp: 162 },
                { label: "Mar", rev: 256, exp: 168 }, { label: "Apr", rev: 268, exp: 165 }, { label: "May", rev: 284, exp: 173 },
              ]} />
            <div style={{ display: "flex", gap: 18, marginTop: 10, fontSize: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--brand)" }} />Revenue</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: "var(--surface-3)", border: "1px solid var(--line)" }} />Expenses</span>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-h"><div className="card-t">Expense breakdown</div><span className="card-sub">This month</span></div>
          <div className="card-b" style={{ display: "flex", alignItems: "center", gap: 22 }}>
            <Donut size={150} thickness={24}
              data={[{ value: 42, color: "var(--brand)" }, { value: 24, color: "var(--info)" }, { value: 18, color: "var(--violet)" }, { value: 16, color: "var(--ink-5)" }]}
              center={<><div className="num" style={{ fontSize: 20, fontWeight: 600 }}>$173k</div><div style={{ fontSize: 10, color: "var(--ink-4)" }}>total</div></>} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 11 }}>
              {([["Payroll", 42, "var(--brand)", "$72.6k"], ["Infrastructure", 24, "var(--info)", "$41.5k"], ["Logistics", 18, "var(--violet)", "$31.1k"], ["Other", 16, "var(--ink-5)", "$27.7k"]] as const).map((r) => (
                <div key={r[0]} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: r[2] }} />
                  <span style={{ fontSize: 12.5, flex: 1, color: "var(--ink-2)" }}>{r[0]}</span>
                  <span className="num" style={{ fontSize: 12.5, fontWeight: 600 }}>{r[3]}</span>
                  <span className="num" style={{ fontSize: 11.5, color: "var(--ink-4)", width: 30, textAlign: "right" }}>{r[1]}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <ListCard title="Upcoming collections" icon="arrow-down" tint="var(--pos)" rows={d.collections} />
        <ListCard title="Upcoming payments" icon="arrow-up" tint="var(--neg)" rows={d.obligations} />
      </div>
    </div>
  );
};

/* ═══ FINANCE · KPIs — single source of truth ═══════════════════════ */
export const FinKPIs = () => {
  const notify = useNotImplemented();
  const [cat, setCat] = useState("All");
  const cats = ["All", ...Array.from(new Set(DATA.kpis.map((k) => k.cat)))];
  const rows = DATA.kpis.filter((k) => cat === "All" || k.cat === cat);
  return (
    <div style={{ maxWidth: 1160, margin: "0 auto" }} className="fade-up">
      <SectionHead title="KPIs" size="lg"
        sub="The single source of truth for every metric in BlinkBooks. Every KPI shown anywhere else in the product comes from here."
        actions={<>
          <button className="btn btn-sec" onClick={() => { posthog.capture("kpi_custom_clicked"); notify("Custom KPI"); }}><Icon name="sliders" size={15} stroke="var(--ink-3)" /> Custom KPI</button>
          <button className="btn btn-sec" onClick={() => { posthog.capture("finance_export_clicked", { page: "kpis" }); notify("Export"); }}><Icon name="download" size={15} stroke="var(--ink-3)" /> Export</button>
        </>} />
      <AiSummary>
        Most KPIs are on target. Two need attention: <b>collection rate</b> slipped to 91.3% (target 95%) and <b>DSO</b> rose to
        38 days — both trace back to the same three overdue invoices. Profitability and growth metrics all beat their targets.
      </AiSummary>
      <div style={{ display: "flex", gap: 7, marginBottom: 16, flexWrap: "wrap" }}>
        {cats.map((c) => (
          <button key={c} onClick={() => { posthog.capture("kpi_category_filtered", { category: c }); setCat(c); }} className={"chip" + (cat === c ? "" : " chip-out")}
            style={cat === c ? { background: "var(--brand)", color: "#fff" } : {}}>{c}</button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
        {rows.map((k, i) => {
          const invert = (k as { invert?: boolean }).invert;
          return (
            <div key={i} className="card" style={{ padding: 18, cursor: "pointer", transition: "box-shadow .15s,border-color .15s" }}
              onClick={() => { posthog.capture("kpi_card_clicked", { kpi: k.name, category: k.cat }); notify(k.name); }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--sh-md)"; e.currentTarget.style.borderColor = "#C9DBE0"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--line)"; }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{k.name}</span>
                <Delta v={k.delta} invert={invert} />
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-4)" }}>{k.cat}</div>
              <div className="num" style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-.02em", margin: "10px 0 2px" }}>{k.value}</div>
              <div style={{ marginTop: 10 }}>
                <Sparkline data={k.spark} color={invert ? (k.delta < 0 ? "var(--pos)" : "var(--neg)") : (k.delta >= 0 ? "var(--pos)" : "var(--neg)")} h={30} />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, paddingTop: 11, borderTop: "1px solid var(--line-2)" }}>
                <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>Target <span style={{ fontWeight: 600, color: "var(--ink-3)" }}>{k.target}</span></span>
                <span className="link" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 3 }}
                  onClick={(e) => { e.stopPropagation(); posthog.capture("kpi_drill_down_clicked", { kpi: k.name }); notify(`Drill down — ${k.name}`); }}>Drill down <Icon name="arrow-right" size={12} /></span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══ FINANCE · CASH & FORECAST (merged, 2 sub-tabs) ════════════════ */
const CashCurrent = () => {
  const d = DATA;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <AiSummary style={{ marginBottom: 0 }}>
        Cash remains healthy — <b>17.5 months</b> of runway at current burn. The WeWork rent payment on May 15 creates a
        temporary dip, fully recovered by the Stripe payout and Acme collection the following week.
      </AiSummary>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {([["Cash on hand", "$842,310", -2.8, true, "MoM"], ["Net cash flow · 30d", "+$36,210", 12.4, false, ""], ["Burn rate", "$48,200/mo", null, false, "trailing 3mo"], ["Runway", "17.5 months", null, false, "at current burn"]] as const).map((k, i) => (
          <div key={i} className="card" style={{ padding: 16 }}>
            <div className="eyebrow" style={{ fontSize: 10.5, marginBottom: 8 }}>{k[0]}</div>
            <div className="num" style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-.02em" }}>{k[1]}</div>
            <div style={{ marginTop: 5, fontSize: 11.5 }}>
              {k[2] != null
                ? <span style={{ color: (k[3] ? k[2] < 0 : k[2] >= 0) ? "var(--pos)" : "var(--neg)", fontWeight: 600 }}>{k[2] >= 0 ? "+" : "−"}{Math.abs(k[2])}% {k[4]}</span>
                : <span style={{ color: "var(--ink-4)" }}>{k[4]}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Inflows vs outflows */}
      <div className="card">
        <div className="card-h">
          <div>
            <div className="card-t">Inflows vs. outflows</div>
            <div className="card-sub">Historical 12w + projected 8w · Granularity: weekly (auto) · $ thousands</div>
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 11, borderRadius: 3, background: "#2f6bff" }} />Inflows</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 11, borderRadius: 3, background: "#DC2626" }} />Outflows</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 2, background: "#16A34A" }} />Net</span>
          </div>
        </div>
        <div className="card-b"><FlowChart data={d.flow} forecastFrom={12} height={300} yFmt={(v) => Math.round(v) + "k"} /></div>
      </div>

      {/* Top expense categories + Upcoming */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="card">
          <div className="card-h"><div className="card-t">Top expense categories · 30d</div></div>
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 15 }}>
            {d.expenseCats.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ width: 80, fontSize: 13, color: "var(--ink-2)", flexShrink: 0 }}>{c.name}</span>
                <div style={{ flex: 1 }}><Bar pct={c.pct} color={c.color} h={9} /></div>
                <span className="num" style={{ fontSize: 13.5, fontWeight: 700, width: 74, textAlign: "right" }}>{fmtUSD(c.amt)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-h"><div className="card-t">Upcoming · next 14 days</div></div>
          <div>
            {d.upcoming14.map((u, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", padding: "13px 18px", borderBottom: i < d.upcoming14.length - 1 ? "1px solid var(--line-2)" : "none" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{u.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 1 }}>{u.date}</div>
                </div>
                <span className="num" style={{ fontSize: 14, fontWeight: 700, color: u.amt >= 0 ? "var(--pos)" : "var(--ink)" }}>
                  {u.amt >= 0 ? "+" : "−"}${Math.abs(u.amt).toLocaleString("en-US")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cash by account */}
      <div className="card">
        <div className="card-h"><div className="card-t">Cash by account</div></div>
        <div className="card-b" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 22 }}>
          {d.cashAccounts.map((a, i) => (
            <div key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 7 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: a.color }} />{a.name}
                </span>
              </div>
              <div className="num" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{fmtUSD(a.bal)}</div>
              <Bar pct={a.pct} color={a.color} h={6} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CashForecast = ({ onOpenWhatIf }: { onOpenWhatIf: () => void }) => {
  const notify = useNotImplemented();
  const d = DATA;
  const [scenario, setScenario] = useState("base");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="card ai-surface" style={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <span className="ai-mark"><Icon name="spark" size={14} stroke="#fff" /></span>
        <div style={{ flex: 1, fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.5 }}>
          <b>Forecast Engine:</b> On the base case, cash reaches <b className="num">$1.03M</b> by week 16 with 91% confidence.
          Hiring 3 engineers in Q3 shortens runway to <b>11.4 months</b>.
        </div>
        <button className="btn btn-sec btn-sm" onClick={() => { posthog.capture("forecast_assumptions_clicked"); notify("View assumptions"); }}>View assumptions</button>
      </div>
      <div className="card">
        <div className="card-h">
          <div className="card-t">Projected cash — 16 weeks</div>
          <Segmented size="sm" value={scenario} onChange={(v) => { posthog.capture("forecast_scenario_changed", { scenario: v }); setScenario(v); }}
            options={[{ value: "base", label: "Base" }, { value: "hiring", label: "+3 hires" }, { value: "downside", label: "Downside" }]} />
        </div>
        <div className="card-b">
          <LineChart height={280} forecastFrom={4} yFmt={(v) => fmtK(v)}
            series={[{
              data: d.cashSeries.projected.map((v, i) => v * 1000 * (scenario === "hiring" && i > 4 ? 0.93 : scenario === "downside" && i > 4 ? 0.85 : 1)),
              color: "var(--brand)",
            }]}
            labels={d.cashSeries.labels} />
          <div style={{ display: "flex", gap: 20, marginTop: 10, fontSize: 12 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 18, height: 2, background: "var(--brand)" }} />Actual</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 18, height: 2, background: "var(--brand)", backgroundImage: "repeating-linear-gradient(90deg,var(--brand) 0 3px,transparent 3px 7px)" }} />Forecast
            </span>
          </div>
        </div>
      </div>
      {/* What-If Scenarios — strategic decision modeling on live financials */}
      <div className="card" style={{ padding: "18px 20px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer", transition: "box-shadow .15s,border-color .15s" }}
        onClick={() => { posthog.capture("whatif_opened", { via: "card" }); onOpenWhatIf(); }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--sh-md)"; e.currentTarget.style.borderColor = "#C9DBE0"; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--line)"; }}>
        <span style={{ width: 42, height: 42, borderRadius: 11, background: "#b8860b18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name="brain" size={21} stroke="#b8860b" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14.5, fontWeight: 600 }}>What-If Scenarios</span>
            <span className="badge b-brand">AI</span>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 3, lineHeight: 1.5 }}>
            Model a strategic decision — a hire, an agency, a campaign — on your live financials and compare runway, ROI and break-even for each path.
          </div>
        </div>
        <button className="btn btn-pri" style={{ flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); posthog.capture("whatif_opened", { via: "button" }); onOpenWhatIf(); }}>
          Open scenario builder
        </button>
      </div>

      {/* Budget vs. forecast — where the plan and the model disagree */}
      <div className="card">
        <div className="card-h">
          <div>
            <div className="card-t">Budget vs. forecast — June</div>
            <div className="card-sub">Where the AI forecast disagrees with the plan. Variances over 10% are flagged.</div>
          </div>
          <span className="link" style={{ fontSize: 12.5, cursor: "pointer" }} onClick={() => { posthog.capture("budget_edit_clicked"); notify("Edit budget"); }}>Edit budget</span>
        </div>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 15 }}>
          {d.budgetVsForecast.map((b, i) => {
            const varPct = Math.round(((b.forecast - b.budget) / b.budget) * 100);
            const over = varPct > 0;
            const flag = Math.abs(varPct) > 10;
            const max = Math.max(...d.budgetVsForecast.map((x) => Math.max(x.budget, x.forecast)));
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ width: 96, fontSize: 13, color: "var(--ink-2)", flexShrink: 0 }}>{b.name}</span>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                  <Bar pct={(b.budget / max) * 100} color="var(--ink-5)" h={6} />
                  <Bar pct={(b.forecast / max) * 100} color={flag && over ? "var(--warn)" : "var(--brand)"} h={6} />
                </div>
                <span className="num" style={{ fontSize: 12.5, color: "var(--ink-4)", width: 66, textAlign: "right" }}>{fmtUSD(b.budget)}</span>
                <span className="num" style={{ fontSize: 13, fontWeight: 600, width: 66, textAlign: "right" }}>{fmtUSD(b.forecast)}</span>
                <span className={"badge " + (flag ? (over ? "b-warn" : "b-pos") : "b-mute")} style={{ width: 58, justifyContent: "center" }}>
                  {over ? "+" : "−"}{Math.abs(varPct)}%
                </span>
              </div>
            );
          })}
          <div style={{ display: "flex", gap: 18, fontSize: 12, color: "var(--ink-3)", paddingTop: 4 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 6, borderRadius: 3, background: "var(--ink-5)" }} />Budget</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 6, borderRadius: 3, background: "var(--brand)" }} />AI forecast</span>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
        {([["Revenue forecast", "$312k", "next month", 8.9, false], ["Expense forecast", "$181k", "next month", 4.6, true], ["Projected net", "$131k", "next month", 17.4, false]] as const).map((k, i) => (
          <div key={i} className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{k[0]}</div>
            <div className="num" style={{ fontSize: 22, fontWeight: 600, margin: "6px 0 2px" }}>{k[1]}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{k[2]}</span>
              <Delta v={k[3]} invert={k[4]} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FinCash = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Ask AI's "Open in scenario builder" deep-links here as /finance/cash?view=whatif
  const fromUrl = new URLSearchParams(location.search).get("view") === "whatif";
  const [sub, setSub] = useState(fromUrl ? "forecast" : "current");
  const [whatIf, setWhatIf] = useState(fromUrl);
  useEffect(() => {
    if (new URLSearchParams(location.search).get("view") === "whatif") { setSub("forecast"); setWhatIf(true); }
  }, [location.search]);
  if (whatIf) return <WhatIfScreen onBack={() => { setWhatIf(false); navigate("/finance/cash", { replace: true }); }} />;
  return (
    <div style={{ maxWidth: 1160, margin: "0 auto" }} className="fade-up">
      <SectionHead title="Cash & Forecast" size="lg"
        sub="Current liquidity and future liquidity — two views of the same problem, in one place."
        actions={<Segmented value={sub} onChange={(v) => { posthog.capture("cash_view_changed", { view: v }); setSub(v); }} options={[{ value: "current", label: "Current Cash" }, { value: "forecast", label: "Forecast" }]} />} />
      {sub === "current" ? <CashCurrent /> : <CashForecast onOpenWhatIf={() => setWhatIf(true)} />}
    </div>
  );
};
